import React, {Suspense} from "react";
import type {RouteRecord} from "vite-react-ssg";
import {SiteNavigate} from "nav/SiteLink";
import {REQUESTED_FILES_ROUTE} from "transfers/transfersTabs";
import RootLayout from "./RootLayout";
import {PanelLoading} from "components/FeaturePanel";
// The PUBLIC, PRE-RENDERED routes are loaded EAGERLY (not `lazy`) so their component is present in the
// main bundle synchronously at hydration, and their code is not a second thing a slow network can make
// the browser wait for (ui#66 for the landing, ui#67 for the rest).
// Eager only the LIGHT public routes. `download/:id` + `reminder-uploads` stay lazy: their page code
// statically pulls the heavy crypto + WebRTC (`@kitecorp/web-peer`) + decrypt-stream machinery, which
// must NOT land in the main bundle every visitor downloads — eager-loading them bloated `app.js` from
// ~360 KB to ~1.4 MB. Only the PRIVATE, client-only routes below are lazy for a different reason: they
// are never pre-rendered, and their heavy P2P/account code should not be in the main bundle either.
//
// ui#1165 correction — eagerness is NOT what settles hydration. vite-react-ssg resolves every matched
// lazy route module BEFORE it builds the router, so a lazy chunk cannot arrive mid-hydration; and the
// react-router warning that used to be read as the lazy-route signature — "No HydrateFallback element
// provided to render during initial hydration" — belongs to the router's initial load. ui#1168 removes
// that load by completing React Router's inline hydration record in `src/utils/routerHydrationData.ts`
// before the browser router starts. The choice here is about bundle weight and network round trips.
import {Component as HomeRouteComponent} from "./pages/HomeRoute";
import {Component as AboutRouteComponent} from "./pages/AboutRoute";
import {Component as FeaturesRouteComponent} from "./pages/FeaturesRoute";
import {Component as PricingRouteComponent} from "./pages/PricingRoute";
import {Component as LoginRouteComponent} from "./pages/LoginRoute";
// Legal routes (ui#97): text-only, so eager (like about/pricing) without weighing down the main bundle.
import {Component as TermsRouteComponent} from "./pages/TermsRoute";
import {Component as PrivacyRouteComponent} from "./pages/PrivacyRoute";
import {Component as CookiesRouteComponent} from "./pages/CookiesRoute";
// The § 312k BGB cancellation page (kitecorp/transferify-ui#2327). Eager and PRE-RENDERED like the legal
// routes above it: Abs. 2 S. 4 requires it to be permanently available and easily accessible, so a direct
// hit must answer real HTML with a 200 to anyone, with no session and no client-side gate.
import {Component as CancelContractRouteComponent} from "./pages/CancelContractRoute";
import {Component as ImpressumRouteComponent} from "./pages/ImpressumRoute";
import {Component as AdvertisingTermsRouteComponent} from "./pages/AdvertisingTermsRoute";
import {Component as DataDeletionRouteComponent} from "./pages/DataDeletionRoute";
import {Component as UnsubscribeRouteComponent} from "./pages/UnsubscribeRoute";
import {Component as MarketingUnsubscribeRouteComponent} from "./pages/MarketingUnsubscribeRoute";
// Ad-landing / explainer routes (ui#133): text-only marketing pages, so eager like about/pricing/legal.
import {Component as ContactRouteComponent} from "./pages/ContactRoute";
import {Component as ZeroKnowledgeRouteComponent} from "./pages/ZeroKnowledgeRoute";
import {Component as P2PRouteComponent} from "./pages/P2PRoute";
import {Component as ClientServerRouteComponent} from "./pages/ClientServerRoute";
// Self-serve advertising booking page (ui#496): public and pre-rendered, so EAGER like the rest of the
// light public tree. Its animated previews reuse framer-motion, which RootLayout -> AppProviders already
// puts in the eager bundle for every page, so lazy-loading it would buy no bundle weight back.
import {Component as SponsorRouteComponent} from "./pages/SponsorRoute";
// The advertising PRESENTATION page. Eager like its `/p2p` and `/zero-knowledge` siblings, and for the
// same reason: it adds no module the home bundle does not already carry. Since ui#1156 that reason is
// worth stating rather than assuming — the page is no longer text-only, its hero mounts the real
// sponsor takeover, and those modules are already eager via `/advertise` below.
import {Component as AdvertisingRouteComponent} from "./pages/AdvertisingRoute";
// The `/sponsor` -> `/advertise` redirect shell. Eager and tiny — it is `<Seo noindex>` plus a
// `<Navigate>`, and the one moment it matters is the moment somebody follows an old link.
import {Component as SponsorRedirectRouteComponent} from "./pages/SponsorRedirectRoute";
// The top bar's Personalization sheet, given a route of its own (ui#1292). PRIVATE and client-only, but
// eager rather than lazy — see the route module for why a chunk would buy nothing here.
import {Component as PersonalizationRouteComponent} from "./pages/PersonalizationRoute";
// MARKDOWN CONTENT pages (ui#1004): one route per file under `src/content/{en,ro}`, appended to both
// trees below. `virtual:content-index` is generated by `vite.contentIndex.ts` and carries only each
// page's key, locale, path and title — the four facts a route table and an hreflang pairing need
// before a page is opened. Everything else (description, FAQ, body) arrives through the page's own
// lazily-fetched `?raw` chunk, which is what keeps `app.js` flat as the content plan adds ~20 pages
// per language. These are the ONE part of the public tree that must NOT be eager: unlike the pages
// above, their weight grows with the corpus.
import {CONTENT_PAGES} from "virtual:content-index";
import {contentRouteRecords} from "content/contentRoutes";
import {prefixedTreeContentEntries, rootTreeContentEntries} from "content/contentTrees";
import {hostnameLocale} from "i18n/localePreference";
import {ContentRouteError} from "./components/ContentRouteError";
import {RootRouteError} from "components/RootRouteError";
import type {ContentPageEntry} from "content/contentPages";

// PRIVATE-route chunks as `React.lazy` components (#146). Each route below wraps one in a `Suspense`
// boundary showing the in-panel {@link PanelLoading} skeleton while the chunk is fetched, so the
// first open never blanks the shell. These import the SAME modules as `nav/preloadRoutes` (`./pages/X`
// here, `../pages/X` there — both resolving to `src/pages/X`), so Vite emits ONE chunk per route
// (dedup by resolved module id, verified in the build): a hover/idle prefetch warms the very module
// the route renders, and the heavy private code stays OUT of the eager bundle. The route modules
// export a named `Component`, remapped here to the `default` React.lazy expects.
const LazyAccountRoute = React.lazy(() => import("./pages/AccountRoute").then((m) => ({default: m.Component})));
const LazyTransfersRoute = React.lazy(() => import("./pages/TransfersRoute").then((m) => ({default: m.Component})));
const LazyContactsRoute = React.lazy(() => import("./pages/ContactsRoute").then((m) => ({default: m.Component})));
const LazyAdminRoute = React.lazy(() => import("./pages/AdminRoute").then((m) => ({default: m.Component})));

/**
 * Wrap a lazy FEATURE-PANEL route (ui#150) in the in-panel {@link PanelLoading} ghost instead of the
 * full-page {@link FeatureLoading}: these routes render inside the docked panel body (via the App
 * shell's Outlet), so their chunk-loading skeleton must be the panel's framed ghost, not a full-screen
 * backdrop. The routes stay `lazy` (heavy feature code out of the eager bundle) — only the panel FRAME
 * is eager, in the App shell.
 */
function withPanelFallback(node: React.ReactNode): React.ReactElement {
	return <Suspense fallback={<PanelLoading/>}>{node}</Suspense>;
}

/**
 * The module vite-react-ssg preloads for a content route, and the loader that resolves one.
 *
 * Both trees share the single {@link ../pages/ContentArticleRoute.tsx} module: a page's identity is
 * DATA (the entry), not a component, so the corpus adds markdown chunks rather than route modules.
 */
const CONTENT_ROUTE_ENTRY = "src/pages/ContentArticleRoute.tsx";

/**
 * Where a rejected content `lazy()` lands. Eager and tiny (it reuses the chrome the text pages
 * already carry), because the one moment it is needed is the one moment a chunk fetch is failing.
 */
const CONTENT_ROUTE_ERROR = <ContentRouteError/>;

const loadContentPage = (entry: ContentPageEntry) =>
	import("./pages/ContentArticleRoute").then((module) => module.contentArticleRoute(entry, CONTENT_PAGES));

/**
 * The locale THIS HOST serves at clean paths: `ro` on transferify.ro, `en` everywhere else — and
 * `en` during pre-rendering, where there is no `window` (see `hostnameLocale`). It decides which
 * content pages the ROOT tree below claims, because transferify.ro serves the Romanian tree at clean
 * paths while the client router matches those paths against the root tree. Read once at module scope,
 * exactly like the LocaleProvider's own first-render resolution, so the table is fixed before the
 * first match and hydration cannot see a different one.
 */
const HOST_LOCALE = hostnameLocale();

/**
 * Application route table, shared by the client SPA and by `vite-react-ssg` for static
 * pre-rendering.
 *
 * - PUBLIC routes (home, about, pricing, login, the download landing shell and the public
 *   reminder-upload pages) are pre-rendered to real HTML for SEO + first paint. Each
 *   public page wrapper renders a `<Seo/>` block through vite-react-ssg's Head bridge alongside the
 *   page component, so crawler-visible metadata is serialized into the document head.
 * - PRIVATE routes (`/account`, `/transfers`, `/transfers/requested`, `/transfers/files`, `/contacts`, `/activate/:token`) stay
 *   client-side rendered behind Firebase auth — they have no SEO value. They are excluded from
 *   pre-rendering via `includedRoutes` in vite.config.ts and rendered lazily in the browser.
 *
 * `lazy` keeps each page in its own chunk so pre-rendering a given path only pulls in that page's
 * code, and the heavy private/P2P modules are never imported while generating the static HTML.
 */
export const routes: RouteRecord[] = [
	{
		path: "/",
		element: <RootLayout/>,
		errorElement: <RootRouteError/>,
		entry: "src/RootLayout.tsx",
		children: [
			// ---- Public, pre-rendered ----
			{
				index: true,
				Component: HomeRouteComponent,
				entry: "src/pages/HomeRoute.tsx",
			},
			{
				path: "about",
				Component: AboutRouteComponent,
				entry: "src/pages/AboutRoute.tsx",
			},
			{
				path: "features",
				Component: FeaturesRouteComponent,
				entry: "src/pages/FeaturesRoute.tsx",
			},
			{
				path: "pricing",
				Component: PricingRouteComponent,
				entry: "src/pages/PricingRoute.tsx",
			},
			{
				path: "terms",
				Component: TermsRouteComponent,
				entry: "src/pages/TermsRoute.tsx",
			},
			{
				path: "privacy",
				Component: PrivacyRouteComponent,
				entry: "src/pages/PrivacyRoute.tsx",
			},
			{
				path: "cookies",
				Component: CookiesRouteComponent,
				entry: "src/pages/CookiesRoute.tsx",
			},
			{
				// The statutory cancellation page. NEVER in CLIENT_ONLY_ROUTE_SEGMENTS — see the route wrapper.
				path: "cancel-contract",
				Component: CancelContractRouteComponent,
				entry: "src/pages/CancelContractRoute.tsx",
			},
			{
				// Provider identification (kitecorp/transferify-ui#2291). Mandatory on the German brand
				// under § 5 DDG, and declared in all three trees because the locale mirrors must claim
				// identical paths and transferify.de hydrates the ROOT tree at clean paths.
				path: "impressum",
				Component: ImpressumRouteComponent,
				entry: "src/pages/ImpressumRoute.tsx",
			},
			{
				// The Advertising Terms (kitecorp/transferify-ui#2602): the booking contract's own document,
				// a legal route like `/terms`. Literal here for `scripts/routePaths.mjs`, which reads
				// literal paths only; `sponsorRoutes.ADVERTISING_TERMS_PATH` is the same address, and
				// `sponsorRoutes.test.ts` keeps the two in step.
				path: "advertising-terms",
				Component: AdvertisingTermsRouteComponent,
				entry: "src/pages/AdvertisingTermsRoute.tsx",
			},
			{
				// Meta's required Data Deletion Instructions URL (ui#185) — public and pre-rendered, so
				// an App Reviewer reaches it signed out.
				path: "data-deletion",
				Component: DataDeletionRouteComponent,
				entry: "src/pages/DataDeletionRoute.tsx",
			},
			{
				path: "login",
				Component: LoginRouteComponent,
				entry: "src/pages/LoginRoute.tsx",
			},
			{
				// Anonymous opt-out target of the unsubscribe link in recipient-facing emails (#222).
				// Its token travels in the QUERY string, so the path is static: the pre-rendered shell
				// serves every recipient and no CloudFront SPA prefix entry is required.
				path: "unsubscribe",
				Component: UnsubscribeRouteComponent,
				entry: "src/pages/UnsubscribeRoute.tsx",
			},
			{
				// The MARKETING unsubscribe (kitecorp/transferify#163). Static path, token in the query
				// string, so like its sibling it needs no CloudFront SPA prefix entry.
				path: "unsubscribe/offers",
				Component: MarketingUnsubscribeRouteComponent,
				entry: "src/pages/MarketingUnsubscribeRoute.tsx",
			},
			// ---- Ad-landing / explainer pages (ui#133) ----
			{
				path: "contact",
				Component: ContactRouteComponent,
				entry: "src/pages/ContactRoute.tsx",
			},
			{
				path: "zero-knowledge",
				Component: ZeroKnowledgeRouteComponent,
				entry: "src/pages/ZeroKnowledgeRoute.tsx",
			},
			{
				path: "p2p",
				Component: P2PRouteComponent,
				entry: "src/pages/P2PRoute.tsx",
			},
			{
				path: "client-server",
				Component: ClientServerRouteComponent,
				entry: "src/pages/ClientServerRoute.tsx",
			},
			{
				// The advertising presentation page: what the Resources menu and the sitemap point at, and
				// the page that hands off to the booking flow below.
				//
				// Every `path` in this table is a STRING LITERAL, here and in the `/ro` tree, even where a
				// shared constant exists for the same route (`sponsor/sponsorRoutes.ts`). `scripts/routePaths.mjs`
				// parses this file and reads literals only, so an interpolated path would drop out of the
				// content-slug collision guard and out of `content/corpus.test.ts`'s served-path inventory —
				// silently, which is the shape of defect that guard exists to catch. `sponsorRoutes.test.ts`
				// asserts the literals below still match the constants, so the duplication cannot drift.
				path: "advertising",
				Component: AdvertisingRouteComponent,
				entry: "src/pages/AdvertisingRoute.tsx",
			},
			{
				// ui#496: the self-serve booking flow. Public and indexable — it is sold to people who have
				// never used the product. Renamed from `/sponsor` on 2026-08-17.
				path: "advertise",
				Component: SponsorRouteComponent,
				entry: "src/pages/SponsorRoute.tsx",
			},
			{
				// The pre-rename booking path, redirected and `noindex` — see the route module for why both
				// halves matter.
				path: "sponsor",
				Component: SponsorRedirectRouteComponent,
				entry: "src/pages/SponsorRedirectRoute.tsx",
			},
			{
				// Landing shell only is pre-rendered; the per-transfer content is fetched client-side.
				// Kept LAZY: statically imports the heavy decrypt/P2P download machinery (see the note
				// on the imports above) — must not be folded into the main bundle.
				path: "download/:id",
				lazy: () => import("./pages/DownloadRoute"),
				entry: "src/pages/DownloadRoute.tsx",
			},
			{
				// Kept LAZY: pulls the heavy upload/crypto machinery — keep out of the main bundle. The contact is
				// optional: an emailed request's link names its contact, a shared link only the request
				// (kitecorp/transferify-ui#3140).
				path: "reminder-uploads/:reminderId/:contactId?",
				lazy: () => import("./pages/ReminderUploadsRoute"),
				entry: "src/pages/ReminderUploadsRoute.tsx",
			},

			// ---- Private, client-side rendered (not pre-rendered) ----
			{
				path: "activate/:token",
				lazy: () => import("./pages/ActivateRoute"),
			},
			{
				path: "account",
				element: withPanelFallback(<LazyAccountRoute/>),
			},
			{
				// ui#496: the operator review queue. Private and client-only — it is under the "account"
				// segment, so `clientOnlyRouteShell` already excludes it from pre-rendering, and it renders
				// inside the docked FeaturePanel like its siblings (see `featurePanelRoutes`).
				path: "account/admin",
				element: withPanelFallback(<LazyAdminRoute/>),
			},
			{
				// ui#150: renders inside the docked FeaturePanel (App shell) — in-panel ghost fallback.
				path: "transfers",
				element: withPanelFallback(<LazyTransfersRoute/>),
			},
			{
				// ui#3140: the Transfers sheet's Requested tab (file requests). The SAME route component as
				// `/transfers`, so switching tabs swaps the tab inside one mounted page, and the tab is read
				// from the path (`transfers/transfersTabs`).
				path: "transfers/requested",
				element: withPanelFallback(<LazyTransfersRoute/>),
			},
			{
				// kitecorp/transferify-ui#3405: the Files tab, every file the account holds. The same route component
				// again, under the same client-only `/transfers` segment, so the edge needs no change.
				path: "transfers/files",
				element: withPanelFallback(<LazyTransfersRoute/>),
			},
			{
				// ui#150: renders inside the docked FeaturePanel (App shell) — in-panel ghost fallback.
				path: "contacts",
				element: withPanelFallback(<LazyContactsRoute/>),
			},
			{
				// ui#3140: file requests moved under Transfers as its Requested tab. The old route stays a
				// REDIRECT so links already out there keep working. It stays in CLIENT_ONLY_ROUTE_SEGMENTS, in
				// the attribution landing-path pattern and in the devops edge list, all of which still serve it.
				path: "reminders",
				element: <SiteNavigate to={REQUESTED_FILES_ROUTE} replace/>,
			},
			{
				// ui#1292: the top bar's Personalization sheet. Unlike the four above it does NOT render
				// inside the docked FeaturePanel — the sheet is chrome mounted by NavBar over the persistent
				// canvas, and this record contributes the auth gate and the noindex metadata. Being a route
				// at all is what stops the `*` fallback below erasing the URL, and what keeps raising the
				// sheet from evicting a feature route's own sheet (which closed with a `replace`).
				path: "personalization",
				element: <PersonalizationRouteComponent/>,
			},

			// ---- Markdown content pages (ui#1004) ----
			// Appended from the file corpus, so a new page is one markdown file and no edit here. WHICH
			// pages depends on the HOST: transferify.ro serves the Romanian tree at CLEAN paths, so on that
			// host these records are the Romanian corpus — see `content/contentTrees.ts`. Ahead of the
			// catch-all below, which would otherwise swallow every one of them.
			...contentRouteRecords(
				rootTreeContentEntries(HOST_LOCALE, CONTENT_PAGES),
				CONTENT_ROUTE_ENTRY,
				loadContentPage,
				CONTENT_ROUTE_ERROR,
			),

			// Fallback: anything else returns home.
			{
				path: "*",
				element: <SiteNavigate to="/" replace/>,
			},
		],
	},
	// ---- Romanian SSG tree (ui#101) ----
	// A parallel `/ro/*` subtree wrapped in a locale-forcing RootLayout, so ONE `npm run build` emits a
	// fully-Romanian `build/ro/**` alongside the English root tree. These wrappers reuse the EXACT same
	// page components — only the forced locale differs, which flips every `useMessages()`/`<Seo>` in the
	// tree to Romanian. Internal links inside the pages are RELATIVE to the document (`./about`), because
	// this artifact is served both at transferify.ro's clean paths and under transferify.cloud's `/ro`
	// subfolder and must be right in both (ui#2732, `nav/sitePath.ts`); `<Seo>` derives its origin from the
	// locale (transferify.ro), so at runtime transferify.ro serves this tree at clean paths and hydrates
	// identically (hostname → ro). Only the 14 public
	// routes are mirrored — the download/reminder shells stay root-only (ui#101 scope).
	{
		path: "/ro",
		element: <RootLayout forcedLocale="ro"/>,
		errorElement: <RootRouteError forcedLocale="ro"/>,
		entry: "src/RootLayout.tsx",
		children: [
			{index: true, Component: HomeRouteComponent, entry: "src/pages/HomeRoute.tsx"},
			{path: "about", Component: AboutRouteComponent, entry: "src/pages/AboutRoute.tsx"},
			{path: "features", Component: FeaturesRouteComponent, entry: "src/pages/FeaturesRoute.tsx"},
			{path: "pricing", Component: PricingRouteComponent, entry: "src/pages/PricingRoute.tsx"},
			{path: "terms", Component: TermsRouteComponent, entry: "src/pages/TermsRoute.tsx"},
			{path: "privacy", Component: PrivacyRouteComponent, entry: "src/pages/PrivacyRoute.tsx"},
			{path: "cookies", Component: CookiesRouteComponent, entry: "src/pages/CookiesRoute.tsx"},
			{path: "cancel-contract", Component: CancelContractRouteComponent, entry: "src/pages/CancelContractRoute.tsx"},
			{path: "impressum", Component: ImpressumRouteComponent, entry: "src/pages/ImpressumRoute.tsx"},
			{path: "advertising-terms", Component: AdvertisingTermsRouteComponent, entry: "src/pages/AdvertisingTermsRoute.tsx"},
			{path: "data-deletion", Component: DataDeletionRouteComponent, entry: "src/pages/DataDeletionRoute.tsx"},
			{path: "login", Component: LoginRouteComponent, entry: "src/pages/LoginRoute.tsx"},
			{path: "unsubscribe", Component: UnsubscribeRouteComponent, entry: "src/pages/UnsubscribeRoute.tsx"},
			{path: "unsubscribe/offers", Component: MarketingUnsubscribeRouteComponent, entry: "src/pages/MarketingUnsubscribeRoute.tsx"},
			{path: "contact", Component: ContactRouteComponent, entry: "src/pages/ContactRoute.tsx"},
			{path: "zero-knowledge", Component: ZeroKnowledgeRouteComponent, entry: "src/pages/ZeroKnowledgeRoute.tsx"},
			{path: "p2p", Component: P2PRouteComponent, entry: "src/pages/P2PRoute.tsx"},
			{path: "client-server", Component: ClientServerRouteComponent, entry: "src/pages/ClientServerRoute.tsx"},
			{path: "advertising", Component: AdvertisingRouteComponent, entry: "src/pages/AdvertisingRoute.tsx"},
			{path: "advertise", Component: SponsorRouteComponent, entry: "src/pages/SponsorRoute.tsx"},
			// The redirect target is unprefixed, like every other internal link in this tree: transferify.ro
			// serves these records at CLEAN paths, so `/advertise` is the Romanian booking page on the host
			// that matters.
			{path: "sponsor", Component: SponsorRedirectRouteComponent, entry: "src/pages/SponsorRedirectRoute.tsx"},
			// Romanian content pages (ui#1004), from `src/content/ro/**`. Their slugs are LOCALIZED and
			// paired to the English ones by the frontmatter `key`, so `/ro/trimite-fisiere-mari` and
			// `/send-large-files` are the same page in the SEO model and cross-link as alternates. This
			// subtree is what the SSG pre-renders into `build/ro/**` — the artifacts transferify.ro serves.
			...contentRouteRecords(
				prefixedTreeContentEntries("ro", CONTENT_PAGES),
				CONTENT_ROUTE_ENTRY,
				loadContentPage,
				CONTENT_ROUTE_ERROR,
			),
		],
	},
	// ---- German SSG tree (kitecorp/transferify-ui#1498) ----
	// The same mirror the Romanian tree above is, for the German market: one `npm run build` emits a
	// `build/de/**` tree that transferify.de serves at clean paths. It reuses the EXACT same page
	// components — only the forced locale differs — and the German catalogue falls back to English key
	// by key (`src/i18n/de.ts`), so this tree renders complete copy from the day it ships and switches
	// to German as locawise fills it in (kitecorp/transferify-ui#1499).
	//
	// The child list is the Romanian tree's, restated rather than shared: `scripts/routePaths.mjs`
	// reads literal child paths straight out of this file, and a shared const spread in here would make
	// the collision guard skip the whole tree. `routePaths.test.mjs` asserts the mirrors declare
	// identical paths, so the restatement cannot drift.
	{
		path: "/de",
		element: <RootLayout forcedLocale="de"/>,
		errorElement: <RootRouteError forcedLocale="de"/>,
		entry: "src/RootLayout.tsx",
		children: [
			{index: true, Component: HomeRouteComponent, entry: "src/pages/HomeRoute.tsx"},
			{path: "about", Component: AboutRouteComponent, entry: "src/pages/AboutRoute.tsx"},
			{path: "features", Component: FeaturesRouteComponent, entry: "src/pages/FeaturesRoute.tsx"},
			{path: "pricing", Component: PricingRouteComponent, entry: "src/pages/PricingRoute.tsx"},
			{path: "terms", Component: TermsRouteComponent, entry: "src/pages/TermsRoute.tsx"},
			{path: "privacy", Component: PrivacyRouteComponent, entry: "src/pages/PrivacyRoute.tsx"},
			{path: "cookies", Component: CookiesRouteComponent, entry: "src/pages/CookiesRoute.tsx"},
			{path: "cancel-contract", Component: CancelContractRouteComponent, entry: "src/pages/CancelContractRoute.tsx"},
			{path: "impressum", Component: ImpressumRouteComponent, entry: "src/pages/ImpressumRoute.tsx"},
			{path: "advertising-terms", Component: AdvertisingTermsRouteComponent, entry: "src/pages/AdvertisingTermsRoute.tsx"},
			{path: "data-deletion", Component: DataDeletionRouteComponent, entry: "src/pages/DataDeletionRoute.tsx"},
			{path: "login", Component: LoginRouteComponent, entry: "src/pages/LoginRoute.tsx"},
			{path: "unsubscribe", Component: UnsubscribeRouteComponent, entry: "src/pages/UnsubscribeRoute.tsx"},
			{path: "unsubscribe/offers", Component: MarketingUnsubscribeRouteComponent, entry: "src/pages/MarketingUnsubscribeRoute.tsx"},
			{path: "contact", Component: ContactRouteComponent, entry: "src/pages/ContactRoute.tsx"},
			{path: "zero-knowledge", Component: ZeroKnowledgeRouteComponent, entry: "src/pages/ZeroKnowledgeRoute.tsx"},
			{path: "p2p", Component: P2PRouteComponent, entry: "src/pages/P2PRoute.tsx"},
			{path: "client-server", Component: ClientServerRouteComponent, entry: "src/pages/ClientServerRoute.tsx"},
			{path: "advertising", Component: AdvertisingRouteComponent, entry: "src/pages/AdvertisingRoute.tsx"},
			{path: "advertise", Component: SponsorRouteComponent, entry: "src/pages/SponsorRoute.tsx"},
			// The redirect target is unprefixed, like every other internal link in this tree: transferify.ro
			// serves these records at CLEAN paths, so `/advertise` is the Romanian booking page on the host
			// that matters.
			{path: "sponsor", Component: SponsorRedirectRouteComponent, entry: "src/pages/SponsorRedirectRoute.tsx"},
			// German content pages, from `src/content/de/**`. Slugs are LOCALIZED and paired to the English
			// ones by the frontmatter `key`, exactly as the Romanian corpus is (ui#1004).
			...contentRouteRecords(
				prefixedTreeContentEntries("de", CONTENT_PAGES),
				CONTENT_ROUTE_ENTRY,
				loadContentPage,
				CONTENT_ROUTE_ERROR,
			),
		],
	},
];

export default routes;
